/**
 * @engine/vfx — VFX preset builders (single-system group).
 *
 * Pure factories returning VFXGraphData, extracted from VFXGraphAsset.ts.
 * Re-exported from VFXGraphAsset so existing import paths are unchanged.
 */

import type { VFXGraphData } from '../types';
import { regenerateGraphIds } from './_shared';

/**
 * Create a firework VFX graph with preset nodes.
 *
 * Includes:
 * - Burst spawn (50-100 particles)
 * - Random sphere position with small radius
 * - Cone velocity (upward spread)
 * - Gravity and drag
 * - Color over life (orange -> red -> fade)
 * - Size over life (shrink)
 * - Quad output
 */

export function createFireworkVFXGraph(
  name: string = "Golden Explosion",
): VFXGraphData {
  const id = () => crypto.randomUUID();

  // === CORE SYSTEM CONTEXTS ===
  const ctxSpawnCore = id();
  const ctxInitCore = id();
  const ctxUpdateCore = id();
  const ctxOutputCore = id();

  // === SPARK SYSTEM CONTEXTS ===
  const ctxEventSpark = id();
  const ctxInitSpark = id();
  const ctxUpdateSpark = id();
  const ctxOutputSpark = id();

  // === CORE SYSTEM NODES ===
  const spawnBurstId = id();
  const initCoreLifetimeId = id();
  const initCoreVelocityId = id();
  const initCoreSizeId = id();
  const initCoreColorId = id();
  const initCoreAngularId = id();
  const updateCoreGravityId = id();
  const updateCoreDragId = id();
  const updateCoreRotationId = id();
  const updateCoreAlphaId = id();
  const triggerOnDieId = id();
  const outputCoreQuadId = id();

  // === SPARK SYSTEM NODES ===
  const eventTransitId = id();
  const initSparkInheritPosId = id();
  const initSparkVelocityId = id();
  const initSparkLifetimeId = id();
  const initSparkSizeId = id();
  const initSparkColorId = id();
  const updateSparkGravityId = id();
  const updateSparkAlphaId = id();
  const outputSparkQuadId = id();

  return {
    id: id(),
    name,
    nodes: [
      // === CORE SPAWN ===
      {
        id: spawnBurstId,
        type: "vfx.spawn.single",
        position: { x: 50, y: 50 },
        inputValues: { count: 200 },
        contextId: ctxSpawnCore,
      },

      // === CORE INIT ===
      {
        id: initCoreLifetimeId,
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 50 },
        inputValues: { min: 0.8, max: 1.5 },
        contextId: ctxInitCore,
      },
      {
        id: initCoreVelocityId,
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 150 },
        inputValues: { speed: 12, speedVariance: 6 },
        contextId: ctxInitCore,
      },
      {
        id: initCoreSizeId,
        type: "vfx.init.size_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 0.15, max: 0.4 },
        contextId: ctxInitCore,
      },
      {
        id: initCoreColorId,
        type: "vfx.init.color_random",
        position: { x: 50, y: 350 },
        inputValues: {
          gradient: {
            stops: [
              { t: 0, color: { r: 1, g: 0.95, b: 0.6, a: 1 } },
              { t: 0.5, color: { r: 1, g: 0.7, b: 0.2, a: 1 } },
              { t: 1, color: { r: 1, g: 0.5, b: 0.1, a: 1 } },
            ],
          },
        },
        contextId: ctxInitCore,
      },
      {
        id: initCoreAngularId,
        type: "vfx.init.angular_velocity",
        position: { x: 50, y: 450 },
        inputValues: { speed: 180, variance: 360 },
        contextId: ctxInitCore,
      },

      // === CORE UPDATE ===
      {
        id: updateCoreGravityId,
        type: "vfx.update.gravity",
        position: { x: 50, y: 50 },
        inputValues: { force: { x: 0, y: -15, z: 0 } },
        contextId: ctxUpdateCore,
      },
      {
        id: updateCoreDragId,
        type: "vfx.update.drag",
        position: { x: 50, y: 150 },
        inputValues: { coefficient: 1.5 },
        contextId: ctxUpdateCore,
      },
      {
        id: updateCoreRotationId,
        type: "vfx.update.rotation",
        position: { x: 50, y: 250 },
        inputValues: {},
        contextId: ctxUpdateCore,
      },
      {
        id: updateCoreAlphaId,
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 350 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.5, v: 1 },
              { t: 0.8, v: 0.5 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: ctxUpdateCore,
      },
      {
        id: triggerOnDieId,
        type: "vfx.trigger.on_die",
        position: { x: 50, y: 450 },
        inputValues: { count: 8 },
        contextId: ctxUpdateCore,
      },

      // === CORE OUTPUT ===
      {
        id: outputCoreQuadId,
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, facingMode: 0 },
        contextId: ctxOutputCore,
      },

      // === SPARK EVENT ===
      {
        id: eventTransitId,
        type: "vfx.event.transit",
        position: { x: 0, y: 0 },
        inputValues: {},
        contextId: ctxEventSpark,
      },

      // === SPARK INIT ===
      {
        id: initSparkInheritPosId,
        type: "vfx.init.inherit_source_position",
        position: { x: 50, y: 50 },
        inputValues: {},
        contextId: ctxInitSpark,
      },
      {
        id: initSparkVelocityId,
        type: "vfx.init.velocity_random",
        position: { x: 50, y: 150 },
        inputValues: { speed: 4, speedVariance: 2 },
        contextId: ctxInitSpark,
      },
      {
        id: initSparkLifetimeId,
        type: "vfx.init.lifetime_random",
        position: { x: 50, y: 250 },
        inputValues: { min: 0.3, max: 0.8 },
        contextId: ctxInitSpark,
      },
      {
        id: initSparkSizeId,
        type: "vfx.init.size_random",
        position: { x: 50, y: 350 },
        inputValues: { min: 0.05, max: 0.12 },
        contextId: ctxInitSpark,
      },
      {
        id: initSparkColorId,
        type: "vfx.init.color",
        position: { x: 50, y: 450 },
        inputValues: { color: { r: 1, g: 0.95, b: 0.7, a: 1 } },
        contextId: ctxInitSpark,
      },

      // === SPARK UPDATE ===
      {
        id: updateSparkGravityId,
        type: "vfx.update.gravity",
        position: { x: 50, y: 50 },
        inputValues: { force: { x: 0, y: -25, z: 0 } },
        contextId: ctxUpdateSpark,
      },
      {
        id: updateSparkAlphaId,
        type: "vfx.update.alpha_over_life",
        position: { x: 50, y: 150 },
        inputValues: {
          curve: {
            points: [
              { t: 0, v: 1 },
              { t: 0.6, v: 0.8 },
              { t: 1, v: 0 },
            ],
          },
        },
        contextId: ctxUpdateSpark,
      },

      // === SPARK OUTPUT ===
      {
        id: outputSparkQuadId,
        type: "vfx.output.quad",
        position: { x: 50, y: 50 },
        inputValues: { blendMode: 1, facingMode: 0 },
        contextId: ctxOutputSpark,
      },
    ],
    edges: [
      {
        id: id(),
        from: { nodeId: triggerOnDieId, portId: "event" },
        to: { nodeId: eventTransitId, portId: "event_in" },
      },
    ],
    variables: [],
    contexts: [
      // Core system contexts
      {
        id: ctxSpawnCore,
        type: "vfx.spawn",
        position: { x: 50, y: 50 },
        size: { width: 280, height: 120 },
        nodeIds: [spawnBurstId],
        collapsed: false,
      },
      {
        id: ctxInitCore,
        type: "vfx.initialize",
        position: { x: 50, y: 200 },
        size: { width: 280, height: 500 },
        nodeIds: [
          initCoreLifetimeId,
          initCoreVelocityId,
          initCoreSizeId,
          initCoreColorId,
          initCoreAngularId,
        ],
        collapsed: false,
      },
      {
        id: ctxUpdateCore,
        type: "vfx.update",
        position: { x: 50, y: 750 },
        size: { width: 280, height: 500 },
        nodeIds: [
          updateCoreGravityId,
          updateCoreDragId,
          updateCoreRotationId,
          updateCoreAlphaId,
          triggerOnDieId,
        ],
        collapsed: false,
      },
      {
        id: ctxOutputCore,
        type: "vfx.output",
        position: { x: 50, y: 1300 },
        size: { width: 280, height: 120 },
        nodeIds: [outputCoreQuadId],
        collapsed: false,
      },
      // Spark system contexts
      {
        id: ctxEventSpark,
        type: "vfx.event",
        position: { x: 450, y: 750 },
        size: { width: 280, height: 120 },
        nodeIds: [eventTransitId],
        collapsed: false,
      },
      {
        id: ctxInitSpark,
        type: "vfx.initialize",
        position: { x: 450, y: 900 },
        size: { width: 280, height: 500 },
        nodeIds: [
          initSparkInheritPosId,
          initSparkVelocityId,
          initSparkLifetimeId,
          initSparkSizeId,
          initSparkColorId,
        ],
        collapsed: false,
      },
      {
        id: ctxUpdateSpark,
        type: "vfx.update",
        position: { x: 450, y: 1450 },
        size: { width: 280, height: 200 },
        nodeIds: [updateSparkGravityId, updateSparkAlphaId],
        collapsed: false,
      },
      {
        id: ctxOutputSpark,
        type: "vfx.output",
        position: { x: 450, y: 1700 },
        size: { width: 280, height: 120 },
        nodeIds: [outputSparkQuadId],
        collapsed: false,
      },
    ],
    contextFlowEdges: [
      // Core system flow
      {
        id: id(),
        fromContextId: ctxSpawnCore,
        fromPortId: "spawn_event",
        toContextId: ctxInitCore,
        toPortId: "spawn_event",
      },
      {
        id: id(),
        fromContextId: ctxInitCore,
        fromPortId: "particle",
        toContextId: ctxUpdateCore,
        toPortId: "particle",
      },
      {
        id: id(),
        fromContextId: ctxUpdateCore,
        fromPortId: "particle",
        toContextId: ctxOutputCore,
        toPortId: "particle",
      },
      // Spark system flow
      {
        id: id(),
        fromContextId: ctxEventSpark,
        fromPortId: "spawn_event",
        toContextId: ctxInitSpark,
        toPortId: "spawn_event",
      },
      {
        id: id(),
        fromContextId: ctxInitSpark,
        fromPortId: "particle",
        toContextId: ctxUpdateSpark,
        toPortId: "particle",
      },
      {
        id: id(),
        fromContextId: ctxUpdateSpark,
        fromPortId: "particle",
        toContextId: ctxOutputSpark,
        toPortId: "particle",
      },
    ],
    contextSettings: {
      [ctxInitCore]: { capacity: 500, boundsMode: "auto" },
      [ctxInitSpark]: { capacity: 4000, boundsMode: "auto" },
    },
    capacity: {
      maxParticles: 5000,
      boundsMode: "auto",
    },
    simulation: {
      space: "world",
      deltaTimeMode: "deltaTime",
    },
    rendering: {
      sortMode: "byAge",
      blendMode: "additive",
    },
    metadata: {
      version: 1,
      createdAt: new Date().toISOString(),
      engine: "red-pew",
    },
  };
}

// ============================================================================
// VFX TEMPLATES - Steam Deck Style Effects
// ============================================================================


/**
 * Cosmic Aurora - Flowing aurora borealis effect with curl noise and turbulence.
 * 5000 particles, long lifetimes, gradient colors.
 */
